import React, {createContext, useContext, useEffect, useState} from "react";
import {
    desistLogin,
    getLoggedInUser,
    isAdmin,
    isLoggedIn,
    persistLogin
} from "../../../Utilities/LocalStorage/storageUtilities";
import {isObjectNullOrEmpty} from "../../../Utilities/Types/objectUtilities";
import {userSearchModel} from "../../../Containers/User/userFactorySearch";
import {isValidUser} from "../../../Containers/User/userUtilities";
import {userModel} from "../../../Containers/User/userFactory";
import {userApi} from "../../../Containers/User/userApi";
import {useAppContext} from "./AppContext";
import {loanLibraryCatalogSearchModel} from "../../../Containers/Library/LoanLibrary/LoanLibraryCatalog/loanLibraryCatalogSearchFactory";
import {loanLibraryHistorySearchModel} from "../../../Containers/Library/LoanLibrary/LoanLibraryHistory/loanLibraryHistorySearchFactory";
import {loanLibraryLoanSearchModel} from "../../../Containers/Loan/LoanLibrary/LoanManagement/loanLibraryLoanSearchFactory";
import {ownCatalogSearchModel} from "../../../Containers/Library/Own/OwnCatalog/ownCatalogSearchFactory";
import {ownLoanHistorySearchModel} from "../../../Containers/Loan/Own/LoanHistory/ownLoanHistorySearchFactory";
import {ownLoanSearchModel} from "../../../Containers/Loan/Own/LoanManagement/ownLoanSearchFactory";

import type {SearchModel} from "../../../Utilities/Search/searchFactory";

export interface UserContextValue {
    user: Record<string, unknown>;
    userId: string;
    isAdmin: boolean;
    isLoggedIn: boolean;
    fetchUser: () => Promise<unknown>;
    setLoggedInFalse: () => boolean;
    setLoggedInTrue: (user: unknown) => boolean;
    [key: string]: unknown;
}

export const UserContext = createContext<UserContextValue>({} as UserContextValue);
export const useUserContext = (): UserContextValue => useContext(UserContext);

interface UserContextProviderProps {
    children: React.ReactNode;
}

export const UserContextProvider: React.FC<UserContextProviderProps> = ({children}) => {
    const [user, setUser] = useState(userModel());
    const {handleApiCall} = useAppContext();

    const isUserAdmin = isAdmin();
    const isUserLoggedIn = isLoggedIn();
    const userId = getLoggedInUser();
    const searchProps: [string] = [userId];

    const updateSearchCriteria = (userId: string, searchCriteria: SearchModel | null, setSearchCriteria: (val: SearchModel) => void, searchModel: (id: string) => SearchModel) => {
        if (!userId) return;

        const searchCriteriaUserId = searchCriteria ? searchCriteria.pageId : userId.toString();

        if ((isObjectNullOrEmpty(searchCriteria) || searchCriteria?.pageId !== searchCriteriaUserId)) {
            setSearchCriteria(searchModel(userId));
        }
    }

    const [userSearchCriteria, setUserSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetUserSearchCriteria = (criteria: SearchModel) => setUserSearchCriteria(criteria);
    const updateUserSearchCriteria = () => updateSearchCriteria(...searchProps, userSearchCriteria,
        setUserSearchCriteria, userSearchModel);

    //loan library catalog
    const [loanLibraryCatalogSearchCriteria, setLoanLibraryCatalogSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLoanLibraryCatalogSearchCriteria = (criteria: SearchModel) => setLoanLibraryCatalogSearchCriteria(criteria);
    const updateLoanLibraryCatalogSearchCriteria = () => updateSearchCriteria(...searchProps, loanLibraryCatalogSearchCriteria,
        setLoanLibraryCatalogSearchCriteria, loanLibraryCatalogSearchModel);

    //loan library history
    const [loanLibraryHistorySearchCriteria, setLoanLibraryHistorySearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLoanLibraryHistorySearchCriteria = (criteria: SearchModel) => setLoanLibraryHistorySearchCriteria(criteria);
    const updateLoanLibraryHistorySearchCriteria = () => updateSearchCriteria(...searchProps, loanLibraryHistorySearchCriteria,
        setLoanLibraryHistorySearchCriteria, loanLibraryHistorySearchModel);

    //loan library loan management
    const [loanLibraryLoanSearchCriteria, setLoanLibraryLoanSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLoanLibraryLoanSearchCriteria = (criteria: SearchModel) => setLoanLibraryLoanSearchCriteria(criteria);
    const updateLoanLibraryLoanSearchCriteria = () => updateSearchCriteria(...searchProps, loanLibraryLoanSearchCriteria,
        setLoanLibraryLoanSearchCriteria, loanLibraryLoanSearchModel);

    //own catalog
    const [ownSearchCriteria, setOwnSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetOwnSearchCriteria = (criteria: SearchModel) => setOwnSearchCriteria(criteria);
    const updateOwnSearchCriteria = () => updateSearchCriteria(...searchProps, ownSearchCriteria,
        setOwnSearchCriteria, ownCatalogSearchModel);

    //own history
    const [ownLoanHistorySearchCriteria, setOwnLoanHistorySearchCriteria] = useState<SearchModel | null>(null);
    const handleSetOwnLoanHistorySearchCriteria = (criteria: SearchModel) => setOwnLoanHistorySearchCriteria(criteria);
    const updateOwnLoanHistorySearchCriteria = () => updateSearchCriteria(...searchProps, ownLoanHistorySearchCriteria,
        setOwnLoanHistorySearchCriteria, ownLoanHistorySearchModel);

    //own loan management
    const [ownLoanSearchCriteria, setOwnLoanSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetOwnLoanSearchCriteria = (criteria: SearchModel) => setOwnLoanSearchCriteria(criteria);
    const updateOwnLoanSearchCriteria = () => updateSearchCriteria(...searchProps, ownLoanSearchCriteria,
        setOwnLoanSearchCriteria, ownLoanSearchModel);


    const setUserIfValid = (user: unknown) => {
        const validatedUser = userModel(user as Record<string, unknown>);
        if (isValidUser(validatedUser)) {
            setUser(validatedUser);
            return true;
        }

        return false;
    };

    const setLoggedInFalse = () => {
        desistLogin();
        setUser(userModel());
        return false;
    };

    const setLoggedInTrue = (user: unknown) => {
        const userData = user as Record<string, unknown>;
        if (!isValidUser(userData)) return setLoggedInFalse();
        persistLogin(
            userData.userId as string,
            userData.email as string,
            userData.fullName as string,
            userData.roles as unknown[],
            userData.patronRole,
            userData.patronSubType
        );
        setUser(userModel());

        if (userData.notifier) (userData.notifier as { notify: () => void }).notify();

        return true;
    };

    const fetchUser = () => {
        return handleApiCall(userApi.getUser, setUserIfValid, setLoggedInFalse);
    };


    useEffect(() => {
        updateUserSearchCriteria();
        updateLoanLibraryCatalogSearchCriteria();
        updateLoanLibraryHistorySearchCriteria();
        updateLoanLibraryLoanSearchCriteria();
        updateOwnSearchCriteria();
        updateOwnLoanHistorySearchCriteria();
        updateOwnLoanSearchCriteria();
        // eslint-disable-next-line
    }, [userId]);


    return (<UserContext
            value={{
                ownLoanHistorySearchCriteria,
                ownLoanSearchCriteria,
                ownSearchCriteria,
                fetchUser,
                handleSetOwnLoanHistorySearchCriteria,
                handleSetOwnLoanSearchCriteria,
                handleSetOwnSearchCriteria,
                handleSetLoanLibraryCatalogSearchCriteria,
                handleSetLoanLibraryHistorySearchCriteria,
                handleSetLoanLibraryLoanSearchCriteria,
                handleSetUserSearchCriteria,
                isAdmin: isUserAdmin,
                isLoggedIn: isUserLoggedIn,
                loanLibraryCatalogSearchCriteria,
                loanLibraryHistorySearchCriteria,
                loanLibraryLoanSearchCriteria,
                setLoggedInFalse,
                setLoggedInTrue,
                user,
                userId,
                userSearchCriteria,
            }}>
            {children}
        </UserContext>)
};
